import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ChevronDown, Sparkles } from "lucide-react";
import { ConnectionBrandIcon } from "@/components/ConnectionBrandIcon";
import { SlackCompanyAppCard, SlackWorkspaceConnect } from "@/components/SlackCompanyApp";
import { api, type MailAccount } from "@/lib/api";
import { openHrefInNewTab } from "@/lib/openExternalLink";
import { connectAtwMailGrants, startAtwMailConnect } from "@/lib/atwMailConnect";
import {
  connectUrl,
  disconnectCalendarAccount,
  disconnectMailAccount,
  fetchMailAccounts,
  fetchMailProviders,
  mailConnected,
  startGmailConnect,
  type MailProvider,
} from "@/lib/googleConnect";
import { fetchZoomMeetingsStatus, startZoomMeetingsConnect } from "@/lib/meetingsConnect";
import {
  accountsForProvider,
  chatAccountLabel,
  disconnectChatAccount,
  fetchChatAccounts,
  fetchChatStatus,
  providerConfigured,
  startChatConnect,
  type ChatAccount,
  type ChatProviderId,
  type ChatStatus,
} from "@/lib/chatConnect";
import {
  fetchHubAiStatus,
  hubAiErrorMessage,
  hubAiProviderLabel,
  type HubAiStatus,
} from "@/lib/hubAi";
import {
  SLACK_CUSTOMER_CHANGED_EVENT,
  clearSlackClaimResult,
  fetchSlackCustomerStatus,
  isSlackCustomerAccount,
  peekSlackClaimResult,
  type SlackClaimResult,
  type SlackCustomerStatus,
} from "@/lib/slackCustomer";
import {
  isAccountFolderSyncEnabled,
  MAIL_SYNC_FOLDERS_EVENT,
  setAccountFolderSyncEnabled,
} from "@/mail/accountFolders";
import {
  getStoredConnectionDisplayName,
  persistConnectionDisplayName,
  resolveAccountLabel,
} from "@/mail/connectionLabels";
import { cn } from "@/lib/utils";

type CatalogProvider = {
  id: string;
  name: string;
  brand: string;
  description: string;
  demo?: boolean;
  liveGoogle?: boolean;
  liveAtw?: boolean;
};

const CATALOG: CatalogProvider[] = [
  {
    id: "atw-mail",
    name: "ATW Mail",
    brand: "ATW",
    description: "Your mail.atw.net inbox inside Hub. Uses the ATW account you already signed in with.",
    liveAtw: true,
  },
  {
    id: "zoho",
    name: "Zoho Mail",
    brand: "Zoho",
    description: "Connect Zoho Mail inboxes alongside Google. Demo until OAuth keys.",
    demo: true,
  },
  {
    id: "outlook",
    name: "Outlook",
    brand: "Microsoft",
    description: "Microsoft 365 / Outlook.com mail. Demo until OAuth keys.",
    demo: true,
  },
  {
    id: "gmail",
    name: "Google",
    brand: "Google",
    description:
      "Connect Gmail so Hub can show that inbox next to the others. Live Google OAuth ready. Connect another Google account without replacing existing inboxes.",
    liveGoogle: true,
  },
  {
    id: "yahoo",
    name: "Yahoo",
    brand: "Yahoo",
    description: "Yahoo Mail inbox. Demo until OAuth keys.",
    demo: true,
  },
];

export const CONNECTIONS_TABS = [
  { id: "connected", label: "Connected", path: "/settings/connections", end: true },
  { id: "mail", label: "Mail", path: "/settings/connections/mail" },
  { id: "chat", label: "Chat", path: "/settings/connections/chat" },
  { id: "calendar", label: "Calendar", path: "/settings/connections/calendar" },
  { id: "meetings", label: "Meetings", path: "/settings/connections/meetings" },
  { id: "ai", label: "AI", path: "/settings/connections/ai" },
] as const;

type ConnectionsCtx = {
  accounts: MailAccount[];
  providers: MailProvider[];
  error: string;
  notice: string;
  busy: string;
  loading: boolean;
  setError: (v: string) => void;
  setNotice: (v: string) => void;
  refresh: () => Promise<void>;
  startGoogleConnect: (opts?: {
    email?: string;
    includeCalendar?: boolean;
    services?: Partial<{ mail: boolean; calendar: boolean; chat: boolean; meetings: boolean }>;
  }) => Promise<void>;
  startAtwMailConnect: (opts?: {
    returnPath?: "/settings/connections/mail" | "/settings/connections/calendar";
  }) => Promise<void>;
  disconnectMail: (account: MailAccount) => Promise<void>;
  disconnectCalendar: (account: MailAccount) => Promise<void>;
  demoConnect: (name: string) => void;
};

const ConnectionsContext = createContext<ConnectionsCtx | null>(null);

function useConnections() {
  const ctx = useContext(ConnectionsContext);
  if (!ctx) throw new Error("useConnections requires ConnectionsLayout");
  return ctx;
}

function connectionsTabFromPath(pathname: string) {
  const exact = CONNECTIONS_TABS.find((tab) => tab.path === pathname);
  if (exact) return exact;
  return (
    CONNECTIONS_TABS.find(
      (tab) => tab.path !== "/settings/connections" && pathname.startsWith(tab.path),
    ) ?? CONNECTIONS_TABS[0]
  );
}

export function ConnectionsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = connectionsTabFromPath(location.pathname);
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [providers, setProviders] = useState<MailProvider[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const [sectionMenuOpen, setSectionMenuOpen] = useState(false);
  const [googleAsk, setGoogleAsk] = useState<null | {
    email?: string;
    services: { mail: boolean; calendar: boolean; chat: boolean; meetings: boolean };
  }>(null);

  const refresh = useCallback(async () => {
    try {
      const [nextAccounts, nextProviders] = await Promise.all([
        fetchMailAccounts(),
        fetchMailProviders(),
      ]);
      setAccounts(nextAccounts);
      setProviders(nextProviders);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load connections.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setNotice("");
    setSectionMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sectionMenuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSectionMenuOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sectionMenuOpen]);

  const startGoogleConnect = useCallback(
    async (opts?: {
      email?: string;
      includeCalendar?: boolean;
      services?: Partial<{ mail: boolean; calendar: boolean; chat: boolean; meetings: boolean }>;
    }) => {
      setError("");
      setNotice("");
      const preset = opts?.services;
      const meetings = Boolean(preset?.meetings);
      setGoogleAsk({
        email: opts?.email,
        services: {
          mail: preset ? Boolean(preset.mail) : true,
          calendar: preset ? Boolean(preset.calendar) || meetings : Boolean(opts?.includeCalendar),
          chat: Boolean(preset?.chat),
          meetings,
        },
      });
    },
    [],
  );

  const confirmGoogleConnect = useCallback(async () => {
    if (!googleAsk) return;
    const { services, email } = googleAsk;
    if (!services.mail && !services.calendar && !services.chat && !services.meetings) {
      setError("Choose at least one: Mail, Calendar, Chat, or Meetings.");
      return;
    }
    setBusy("connect-google");
    setError("");
    setNotice("");
    try {
      const payload = await startGmailConnect({ email, services });

        if (payload.setupRequired) {
          setError(payload.message || "Google OAuth setup is required on the API host.");
          return;
        }
        if (payload.ok === false) {
          setError(payload.message || "Could not start Google connect.");
          return;
        }

        const url = connectUrl(payload);
        if (url) {
          window.location.href = url;
          return;
        }
        if (payload.needsBrowser) {
          setError(
            payload.message ||
              "Google OAuth did not return a sign-in URL. Try again or check API OAuth config.",
          );
          return;
        }
        setNotice(payload.message || "Google connect finished without a browser redirect.");
        setGoogleAsk(null);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not start Google connect.");
      } finally {
        setBusy("");
      }
    },
    [googleAsk, refresh],
  );

  const connectAtwMail = useCallback(
    async (opts?: {
      returnPath?: "/settings/connections/mail" | "/settings/connections/calendar";
    }) => {
      setBusy("connect-atw-mail");
      setError("");
      setNotice("");
      try {
        const result = await startAtwMailConnect(opts);
        setNotice(result.message || "ATW Mail is connected.");
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not connect ATW Mail.");
      } finally {
        setBusy("");
      }
    },
    [refresh],
  );

  const disconnectMail = useCallback(
    async (account: MailAccount) => {
      setBusy(account.id);
      setError("");
      setNotice("");
      try {
        const result = await disconnectMailAccount(account.id);
        setNotice(result.message || `Disconnected ${account.email}.`);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not disconnect mail.");
      } finally {
        setBusy("");
      }
    },
    [refresh],
  );

  const disconnectCalendar = useCallback(
    async (account: MailAccount) => {
      setBusy(`cal-${account.id}`);
      setError("");
      setNotice("");
      try {
        const result = await disconnectCalendarAccount(account.id);
        setNotice(result.message || `Calendar disconnected for ${account.email}.`);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not disconnect Calendar.");
      } finally {
        setBusy("");
      }
    },
    [refresh],
  );

  const demoConnect = useCallback((name: string) => {
    setNotice(`${name} OAuth is demo-only until provider keys are on the API host.`);
  }, []);

  const value = useMemo<ConnectionsCtx>(
    () => ({
      accounts,
      providers,
      error,
      notice,
      busy,
      loading,
      setError,
      setNotice,
      refresh,
      startGoogleConnect,
      startAtwMailConnect: connectAtwMail,
      disconnectMail,
      disconnectCalendar,
      demoConnect,
    }),
    [
      accounts,
      providers,
      error,
      notice,
      busy,
      loading,
      refresh,
      startGoogleConnect,
      connectAtwMail,
      disconnectMail,
      disconnectCalendar,
      demoConnect,
    ],
  );

  return (
    <ConnectionsContext.Provider value={value}>
      <div className="settings-area">
        <h2 className="settings-area-title">Connections</h2>

        {/* Desktop: horizontal Connections tabs (unchanged). */}
        <nav className="connections-tabs desktop-only" aria-label="Connections sections">
          {CONNECTIONS_TABS.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.path}
              end={"end" in tab ? tab.end : false}
              className={({ isActive }) => cn("connections-tab", isActive && "active")}
            >
              <span>{tab.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Phone: subsection dropdown (replaces chip wrap / scroll bar). */}
        <div className="connections-section-select phone-only">
          <button
            type="button"
            className="connections-section-trigger"
            aria-expanded={sectionMenuOpen}
            aria-haspopup="listbox"
            aria-label="Connections section"
            onClick={() => setSectionMenuOpen((open) => !open)}
          >
            <span className="connections-section-trigger-label">{activeTab.label}</span>
            <ChevronDown size={16} aria-hidden />
          </button>
          {sectionMenuOpen ? (
            <>
              <button
                type="button"
                className="connections-section-scrim"
                aria-label="Close Connections sections"
                onClick={() => setSectionMenuOpen(false)}
              />
              <div
                className="connections-section-menu"
                role="listbox"
                aria-label="Connections sections"
              >
                {CONNECTIONS_TABS.map((tab) => {
                  const selected = tab.id === activeTab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={cn("connections-section-option", selected && "active")}
                      onClick={() => {
                        setSectionMenuOpen(false);
                        if (!selected) navigate(tab.path);
                      }}
                    >
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>

        {error ? (
          <p className="banner-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="banner-ok">{notice}</p> : null}

        <Outlet />
        {googleAsk ? (
          <div className="chat-menu-scrim" role="presentation" onClick={() => setGoogleAsk(null)}>
            <div
              className="google-connect-ask"
              role="dialog"
              aria-labelledby="google-connect-title"
              onClick={(event) => event.stopPropagation()}
            >
              <h3 id="google-connect-title">Connect Google</h3>
              <p className="muted">Choose what Hub should connect for this Google account.</p>
              {(
                [
                  ["mail", "Mail"],
                  ["calendar", "Calendar"],
                  ["chat", "Chat"],
                  ["meetings", "Meetings"],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className="google-connect-choice">
                  <input
                    type="checkbox"
                    checked={googleAsk.services[key]}
                    onChange={() =>
                      setGoogleAsk((current) => {
                        if (!current) return current;
                        const services = { ...current.services, [key]: !current.services[key] };
                        if (key === "meetings" && services.meetings) services.calendar = true;
                        if (key === "calendar" && !services.calendar) services.meetings = false;
                        return { ...current, services };
                      })
                    }
                  />
                  {label}
                </label>
              ))}
              <p className="muted tight google-connect-note">
                Chat reads Google Chat spaces. Meetings reads Calendar events that include a Meet link, so
                Meetings also turns Calendar on.
              </p>
              <div className="settings-btn-row">
                <button type="button" className="secondary-btn" onClick={() => setGoogleAsk(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={Boolean(busy)}
                  onClick={() => void confirmGoogleConnect()}
                >
                  {busy === "connect-google" ? "Opening Google…" : "Continue"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </ConnectionsContext.Provider>
  );
}

/** @deprecated Prefer ConnectionsLayout + nested tab routes. */
export function ConnectionsPage() {
  return <Navigate to="/settings/connections" replace />;
}

export function ConnectionsConnectedTab() {
  const {
    accounts,
    busy,
    loading,
    setNotice,
    startGoogleConnect,
    disconnectMail,
    disconnectCalendar,
  } = useConnections();

  const linked = accounts.filter(
    (a) => mailConnected(a) || a.calendarConnected,
  );
  const [editing, setEditing] = useState<MailAccount | null>(null);
  const [editName, setEditName] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");
  const [labelTick, setLabelTick] = useState(0);
  const [folderSyncTick, setFolderSyncTick] = useState(0);

  useEffect(() => {
    function onLabels() {
      setLabelTick((tick) => tick + 1);
    }
    function onFolderSync() {
      setFolderSyncTick((tick) => tick + 1);
    }
    function onStorage() {
      onLabels();
      onFolderSync();
    }
    window.addEventListener("atw-hub-connection-display-names", onLabels);
    window.addEventListener(MAIL_SYNC_FOLDERS_EVENT, onFolderSync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("atw-hub-connection-display-names", onLabels);
      window.removeEventListener(MAIL_SYNC_FOLDERS_EVENT, onFolderSync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  function openEdit(account: MailAccount) {
    setEditing(account);
    setEditName(getStoredConnectionDisplayName(account) || account.displayName || "");
    setEditError("");
  }

  async function saveEdit() {
    if (!editing) return;
    setEditBusy(true);
    setEditError("");
    try {
      await persistConnectionDisplayName(editing.id, editing.email, editName, api);
      setEditing(null);
      setLabelTick((tick) => tick + 1);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Could not save display name.");
    } finally {
      setEditBusy(false);
    }
  }

  void labelTick;
  void folderSyncTick;

  return (
    <section className="settings-card" id="connected-accounts">
      <div className="settings-card-head">
        <h2>Connected</h2>
        <span className="pill">
          {loading
            ? "Loading…"
            : linked.length === 0
              ? "None Connected"
              : `${linked.length} Connected`}
        </span>
      </div>
      {loading ? <p className="muted">Loading connections…</p> : null}
      {!loading && linked.length === 0 ? (
        <div className="connections-empty">
          <p className="muted">
            Nothing linked yet. Connect a mail inbox, calendar, Slack, or Zoom from the tabs
            above.
          </p>
          <div className="settings-btn-row">
            <Link className="primary-btn settings-link-btn" to="/settings/connections/mail">
              Connect Mail
            </Link>
            <Link className="secondary-btn settings-link-btn" to="/settings/connections/chat">
              Set Up Chat
            </Link>
          </div>
        </div>
      ) : (
        <ul className="account-list">
          {accounts.map((account) => {
            const isGmail = (account.providerId || "gmail") === "gmail";
            if (!mailConnected(account) && !account.calendarConnected) return null;
            const label = resolveAccountLabel(account);
            const showEmailUnder = label !== account.email;
            return (
              <li key={account.id} className="account-row">
                <div className="account-row-main">
                  <div className="connection-title">
                    <ConnectionBrandIcon
                      id={account.providerId || "gmail"}
                      label={account.providerId || "Google"}
                    />
                    <div>
                      <strong className="mail-email-truncate" title={account.email}>
                        {label}
                      </strong>
                      {showEmailUnder ? (
                        <p className="muted tight">{account.email}</p>
                      ) : null}
                    </div>
                  </div>
                  <ul className="account-capabilities">
                    <li>
                      <span>Mail</span>
                      <strong className={mailConnected(account) ? "capability-ok" : ""}>
                        {mailConnected(account) ? "Connected" : "Not connected"}
                      </strong>
                    </li>
                    <li>
                      <span>Calendar</span>
                      {account.calendarConnected ? (
                        <strong className="capability-ok">Connected</strong>
                      ) : isGmail ? (
                        <button
                          type="button"
                          className="link-btn"
                          disabled={Boolean(busy)}
                          onClick={() =>
                            void startGoogleConnect({
                              email: account.email,
                              includeCalendar: true,
                            })
                          }
                        >
                          {busy === `cal-${account.email}`
                            ? "Opening Google…"
                            : "Connect Calendar"}
                        </button>
                      ) : (
                        <span className="muted">Not available</span>
                      )}
                    </li>
                    {mailConnected(account) ? (
                      <li className="account-capability-sync">
                        <span>Folders</span>
                        <label className="settings-check compact account-folder-sync">
                          <input
                            type="checkbox"
                            checked={isAccountFolderSyncEnabled(account.id)}
                            aria-label={`Sync provider folders for ${label}`}
                            onChange={(e) => {
                              setAccountFolderSyncEnabled(account.id, e.target.checked);
                              setFolderSyncTick((tick) => tick + 1);
                              setNotice(
                                e.target.checked
                                  ? `Syncing folders for ${label}.`
                                  : `Using standard folders for ${label}.`,
                              );
                            }}
                          />
                          <span>Sync provider folders</span>
                        </label>
                      </li>
                    ) : null}
                  </ul>
                </div>
                <div className="account-row-actions">
                  <button
                    type="button"
                    className="secondary-btn"
                    disabled={Boolean(busy) || editBusy}
                    onClick={() => openEdit(account)}
                  >
                    Edit Display Name
                  </button>
                  {account.calendarConnected ? (
                    <>
                      <button
                        type="button"
                        className="secondary-btn"
                        disabled={Boolean(busy)}
                        onClick={() =>
                          void startGoogleConnect({
                            email: account.email,
                            includeCalendar: true,
                          })
                        }
                      >
                        {busy === `cal-${account.email}`
                          ? "Opening Google…"
                          : "Reconnect Calendar"}
                      </button>
                      <button
                        type="button"
                        className="secondary-btn"
                        disabled={Boolean(busy)}
                        onClick={() => void disconnectCalendar(account)}
                      >
                        {busy === `cal-${account.id}`
                          ? "Disconnecting…"
                          : "Disconnect Calendar"}
                      </button>
                    </>
                  ) : null}
                  {mailConnected(account) ? (
                    <button
                      type="button"
                      className="secondary-btn danger-btn"
                      disabled={Boolean(busy)}
                      onClick={() => void disconnectMail(account)}
                    >
                      {busy === account.id ? "Disconnecting…" : "Disconnect Mail"}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editing ? (
        <div className="conn-edit-scrim" role="presentation" onClick={() => setEditing(null)}>
          <div
            className="conn-edit-modal"
            role="dialog"
            aria-labelledby="conn-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="conn-edit-head">
              <h3 id="conn-edit-title">Display Name</h3>
              <button type="button" className="text-btn" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
            <p className="muted tight">
              Shown in Mail account lists and row pills. Falls back to{" "}
              <code>{editing.email}</code> when empty.
            </p>
            <label className="conn-edit-label">
              Display name
              <input
                type="text"
                value={editName}
                autoFocus
                maxLength={80}
                placeholder={editing.email}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveEdit();
                  if (e.key === "Escape") setEditing(null);
                }}
              />
            </label>
            {editError ? <p className="banner-error">{editError}</p> : null}
            <div className="settings-btn-row">
              <button
                type="button"
                className="primary-btn"
                disabled={editBusy}
                onClick={() => void saveEdit()}
              >
                {editBusy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="secondary-btn"
                disabled={editBusy || !editName.trim()}
                onClick={() => {
                  setEditName("");
                  void (async () => {
                    if (!editing) return;
                    setEditBusy(true);
                    try {
                      await persistConnectionDisplayName(editing.id, editing.email, "", api);
                      setEditing(null);
                      setLabelTick((tick) => tick + 1);
                    } finally {
                      setEditBusy(false);
                    }
                  })();
                }}
              >
                Use Email
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function ConnectionsMailTab() {
  const {
    accounts,
    providers,
    busy,
    loading,
    startGoogleConnect,
    startAtwMailConnect,
    demoConnect,
    setError,
    setNotice,
    refresh,
  } = useConnections();
  const resumedAtwMail = useRef(false);

  useEffect(() => {
    if (resumedAtwMail.current) return;
    const prefix = "#atw-mail=";
    const hash = window.location.hash;
    if (!hash.startsWith(prefix)) return;
    resumedAtwMail.current = true;
    let grants: { email?: string; grant: string }[] = [];
    try {
      const parsed = JSON.parse(decodeURIComponent(hash.slice(prefix.length))) as {
        grants?: { email?: string; grant: string }[];
      };
      grants = Array.isArray(parsed.grants) ? parsed.grants : [];
    } catch {
      setError("ATW Mail did not return a connection.");
      return;
    }
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
    void (async () => {
      try {
        const result = await connectAtwMailGrants(grants);
        setNotice(result.message);
        await refresh();
      } catch (error) {
        setError(error instanceof Error ? error.message : "Could not connect ATW Mail.");
      }
    })();
  }, [refresh, setError, setNotice]);

  const googleConnected = useMemo(
    () => accounts.filter((a) => (a.providerId || "gmail") === "gmail" && mailConnected(a)),
    [accounts],
  );
  const gmailProvider = providers.find((p) => p.id === "gmail");

  return (
    <section className="settings-card" id="connect-mail">
      <div className="settings-card-head">
        <h2>Connect Mail</h2>
        <span className="pill">Provider Catalog</span>
      </div>
      <p className="muted">
        Connect additional inboxes here, including a second Google account. Mail no longer
        offers Add Inbox; use Settings for accounts and these Connections tabs for the full
        provider catalog.
      </p>
      {loading ? <p className="muted">Loading providers…</p> : null}
      {gmailProvider?.oauthReady === false ? (
        <p className="banner-error" role="alert">
          Google OAuth is not ready on the API host. Add client credentials, then retry.
        </p>
      ) : null}
      <div className="provider-cards">
        {CATALOG.map((item) => {
          const connectedCount =
            item.id === "gmail"
              ? googleConnected.length
              : providers.find((p) => p.id === item.id)?.connectedAccountIds?.length || 0;
          const status =
            connectedCount > 0
              ? connectedCount === 1
                ? "1 Inbox Connected"
                : `${connectedCount} Inboxes Connected`
              : "Available";
          return (
            <article key={item.id} className="provider-card">
              <div className="settings-card-head">
                <div className="connection-title">
                  <ConnectionBrandIcon id={item.id} label={item.name} />
                  <div>
                    <h3>{item.name}</h3>
                    <p className="muted tight">{item.brand}</p>
                  </div>
                </div>
                <span className={`pill ${connectedCount ? "pill-connected" : ""}`}>
                  {status}
                </span>
              </div>
              <p className="muted">
                {item.description}
                {item.demo ? " Demo until OAuth keys." : ""}
              </p>
              {item.liveAtw ? (
                <button
                  type="button"
                  className="settings-connect-btn"
                  disabled={Boolean(busy)}
                  onClick={() => void startAtwMailConnect()}
                >
                  {busy === "connect-atw-mail"
                    ? "Connecting…"
                    : connectedCount > 0
                      ? "Reconnect"
                      : "Connect"}
                </button>
              ) : item.liveGoogle ? (
                <button
                  type="button"
                  className="settings-connect-btn"
                  disabled={Boolean(busy) || gmailProvider?.oauthReady === false}
                  onClick={() => void startGoogleConnect()}
                >
                  {busy === "connect-google"
                    ? "Opening Google…"
                    : connectedCount > 0
                      ? "Connect Another Google Account"
                      : "Connect"}
                </button>
              ) : (
                <button
                  type="button"
                  className="settings-connect-btn"
                  onClick={() => demoConnect(item.name)}
                >
                  Connect
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function ConnectionsChatTab() {
  const { accounts: mailAccounts, setNotice, startGoogleConnect, busy } = useConnections();
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [accounts, setAccounts] = useState<ChatAccount[]>([]);
  const [busyProvider, setBusyProvider] = useState<ChatProviderId | "">("");
  const [loadingChat, setLoadingChat] = useState(true);
  const [customer, setCustomer] = useState<SlackCustomerStatus | null>(null);
  const [customerBusy, setCustomerBusy] = useState("");
  const [claimResult, setClaimResult] = useState<SlackClaimResult | null>(peekSlackClaimResult);
  // A company admin's connect link: /settings/connections/chat?slackWorkspace=importgenius
  const [searchParams] = useSearchParams();
  const linkedWorkspace = searchParams.get("slackWorkspace") || "";

  const refreshChat = useCallback(async () => {
    setLoadingChat(true);
    try {
      const [nextStatus, nextAccounts, nextCustomer] = await Promise.all([
        fetchChatStatus().catch(() => null),
        fetchChatAccounts().catch(() => [] as ChatAccount[]),
        fetchSlackCustomerStatus().catch(() => null),
      ]);
      setStatus(nextStatus);
      const merged =
        nextAccounts.length > 0
          ? nextAccounts
          : (nextStatus?.accounts || []).filter((a) => a.connected !== false);
      setAccounts(merged);
      setCustomer(nextCustomer);
    } finally {
      setLoadingChat(false);
    }
  }, []);

  useEffect(() => {
    void refreshChat();
  }, [refreshChat]);

  // Slack's install callback returns here; useSlackCustomerClaim (AppShell) reports the outcome once.
  useEffect(() => {
    clearSlackClaimResult();
    function onClaimed() {
      setClaimResult(peekSlackClaimResult());
      clearSlackClaimResult();
      void refreshChat();
    }
    window.addEventListener(SLACK_CUSTOMER_CHANGED_EVENT, onClaimed);
    return () => window.removeEventListener(SLACK_CUSTOMER_CHANGED_EVENT, onClaimed);
  }, [refreshChat]);

  async function connectProvider(providerId: ChatProviderId) {
    setBusyProvider(providerId);
    try {
      const result = await startChatConnect(providerId, { forceLogin: true });
      const url = connectUrl(result);
      if (url) {
        openHrefInNewTab(url);
        const label =
          providerId === "cliq"
            ? "Zoho Cliq"
            : providerId === "zoom"
              ? "Zoom Team Chat"
              : providerId === "teams"
                ? "Microsoft Teams"
                : "Slack";
        setNotice(
          `Opened ${label} authorization. Finish consent, then refresh — Connect again to add another account.`,
        );
        return;
      }
      if (result.setupRequired || result.message) {
        setNotice(result.message || "Chat OAuth is not ready on the API host yet.");
        return;
      }
      setNotice("Chat connect response had no authorization URL. Check API OAuth config.");
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not start chat connect.");
    } finally {
      setBusyProvider("");
      void refreshChat();
    }
  }

  async function disconnectAccount(accountId: string, providerId: ChatProviderId) {
    setBusyProvider(providerId);
    try {
      await disconnectChatAccount(accountId);
      setNotice("Chat account disconnected.");
      await refreshChat();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not disconnect chat account.");
    } finally {
      setBusyProvider("");
    }
  }

  async function disconnectCustomer(account: ChatAccount) {
    setCustomerBusy(account.id);
    setClaimResult(null);
    try {
      const result = await disconnectChatAccount(account.id);
      setNotice(result.message || `${chatAccountLabel(account)} disconnected.`);
      await refreshChat();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not disconnect Slack.");
    } finally {
      setCustomerBusy("");
    }
  }

  const slackConfigured = providerConfigured(status, "slack");
  const cliqConfigured = providerConfigured(status, "cliq");
  const zoomConfigured = providerConfigured(status, "zoom");
  const teamsConfigured = providerConfigured(status, "teams");
  const allSlackAccounts = accountsForProvider(accounts, "slack");
  const slackAccounts = allSlackAccounts.filter((a) => !isSlackCustomerAccount(a));
  const customerAccounts = allSlackAccounts.filter(isSlackCustomerAccount);
  const cliqAccounts = accountsForProvider(accounts, "cliq");
  const zoomAccounts = accountsForProvider(accounts, "zoom");
  const teamsAccounts = accountsForProvider(accounts, "teams");
  const googleChatAccounts = accountsForProvider(accounts, "google");
  const googleMailChat = mailAccounts.filter(
    (account) => account.chatConnected && (account.providerId === "gmail" || !account.providerId),
  );

  return (
    <>
      <section className="settings-card" id="google-chat">
        <div className="connector-row">
          <div className="connection-title">
            <ConnectionBrandIcon id="google" label="Google Chat" />
            <div>
              <h2>Google Chat</h2>
              <p className="muted tight">Spaces and direct messages from a connected Google account.</p>
            </div>
          </div>
          <span className={`pill ${googleChatAccounts.length || googleMailChat.length ? "pill-connected" : ""}`}>
            {googleChatAccounts.length || googleMailChat.length ? "Connected" : "Not Connected"}
          </span>
        </div>
        {googleChatAccounts.length ? (
          <ul className="simple-list">
            {googleChatAccounts.map((account) => (
              <li key={account.id}>
                <div>
                  <strong>{chatAccountLabel(account)}</strong>
                  {account.email ? <p className="muted tight">{account.email}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">
            Choose Chat in the Google dialog. The Google Cloud project also needs the Chat API enabled
            or consent will fail.
          </p>
        )}
        <div className="settings-btn-row">
          <button
            type="button"
            className="primary-btn"
            disabled={Boolean(busy)}
            onClick={() =>
              void startGoogleConnect({
                services: { mail: false, calendar: false, chat: true, meetings: false },
              })
            }
          >
            {busy === "connect-google" ? "Opening Google…" : "Connect Google Chat"}
          </button>
          <Link className="secondary-btn settings-link-btn" to="/chat">
            Open Chat
          </Link>
        </div>
      </section>

      <section className="settings-card" id="chat-order">
        <h2>Chat connectors</h2>
        <p className="muted">
          Google Chat uses the Google account dialog. Slack, Zoho Cliq, Zoom Team Chat, and Microsoft
          Teams each support multiple logins — Connect again never replaces an existing account.
        </p>
        {loadingChat ? <p className="muted tight">Loading chat accounts…</p> : null}
      </section>

      <section className="settings-card" id="slack">
        <div className="connector-row">
          <div className="connection-title">
            <ConnectionBrandIcon id="slack" label="Slack" />
            <div>
              <h2>Slack</h2>
              <p className="muted tight">Workspace channels and DMs in Hub Chat.</p>
            </div>
          </div>
          <span className="pill">
            {slackAccounts.length
              ? `${slackAccounts.length} connected`
              : slackConfigured
                ? "Ready"
                : "Setup"}
          </span>
        </div>
        {slackAccounts.length ? (
          <ul className="simple-list">
            {slackAccounts.map((account) => (
              <li key={account.id} className="connector-row">
                <div>
                  <strong>{chatAccountLabel(account)}</strong>
                  {account.email ? <p className="muted tight">{account.email}</p> : null}
                </div>
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={Boolean(busyProvider)}
                  onClick={() => void disconnectAccount(account.id, "slack")}
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">
            {slackConfigured
              ? "No Slack login linked yet. Connect one workspace — you can add another afterward."
              : "Credentials are not on the API host yet. Empty setup is expected until secrets land under ~/.config/atw-hub/slack.env (and Lightsail)."}
          </p>
        )}
        {!slackConfigured ? (
          <ol className="settings-steps">
            <li>
              Create an app at <code>api.slack.com/apps</code>.
            </li>
            <li>
              Add redirect URL: <code>https://api.hub.atw.net/oauth/slack/callback</code>.
            </li>
            <li>
              Add user token scopes: <code>channels:history</code>, <code>chat:write</code>,{" "}
              <code>users:read</code>, and related Chat scopes.
            </li>
            <li>
              Copy Client ID / Secret into <code>~/.config/atw-hub/slack.env</code> and the API
              host.
            </li>
            <li>Restart the Hub API and return here to Connect.</li>
          </ol>
        ) : null}
        <div className="settings-btn-row">
          <button
            type="button"
            className="primary-btn"
            disabled={Boolean(busyProvider) || !slackConfigured}
            onClick={() => void connectProvider("slack")}
          >
            {busyProvider === "slack"
              ? "Opening…"
              : slackAccounts.length
                ? "Connect Another Slack"
                : "Connect Slack"}
          </button>
          <Link className="secondary-btn settings-link-btn" to="/chat">
            Open Chat
          </Link>
        </div>
        {slackConfigured ? <SlackWorkspaceConnect initialWorkspace={linkedWorkspace} onNotice={setNotice} /> : null}
      </section>

      <SlackCompanyAppCard onNotice={setNotice} />

      {customer?.configured || customerAccounts.length ? (
        <section className="settings-card" id="slack-app">
          <div className="connector-row">
            <div className="connection-title">
              <ConnectionBrandIcon id="slack" label="Slack" />
              <div>
                <h2>ATW Hub for Slack</h2>
                <p className="muted tight">
                  The ATW Hub app for Slack. Hub loads a conversation when you open it and doesn&apos;t
                  store message text.
                </p>
              </div>
            </div>
            <span className="pill">
              {customerAccounts.length
                ? `${customerAccounts.length} connected`
                : customer?.listed
                  ? "Ready"
                  : "Pilot"}
            </span>
          </div>
          {claimResult ? (
            <p className={claimResult.ok ? "banner-ok" : "banner-error"} role={claimResult.ok ? undefined : "alert"}>
              {claimResult.message}
            </p>
          ) : null}
          {customer && !customer.listed ? (
            <p className="muted tight">
              Pilot: until Slack approves ATW Hub in the Slack Marketplace, Slack sends Hub only the
              latest messages in each conversation, and Needs you on Hub Home stays empty.
            </p>
          ) : null}
          {customerAccounts.length ? (
            <ul className="simple-list">
              {customerAccounts.map((account) => (
                <li key={account.id} className="connector-row">
                  <div>
                    <strong>{chatAccountLabel(account)}</strong>
                    {account.email ? <p className="muted tight">{account.email}</p> : null}
                  </div>
                  <button
                    type="button"
                    className="secondary-btn"
                    disabled={Boolean(customerBusy)}
                    onClick={() => void disconnectCustomer(account)}
                  >
                    {customerBusy === account.id ? "Disconnecting…" : "Disconnect"}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {customer?.configured && customer.installUrl ? (
            <div className="settings-btn-row">
              <a className="primary-btn settings-link-btn" href={customer.installUrl} data-hub-same-tab="1">
                {customerAccounts.length ? "Connect Another Workspace" : "Connect Slack"}
              </a>
            </div>
          ) : null}
          <p className="muted tight">
            Disconnect deletes Hub&apos;s Slack sign-in for that workspace, its read markers, and the
            links you saved with Send to Hub.
          </p>
        </section>
      ) : null}

      <section className="settings-card" id="cliq">
        <div className="connector-row">
          <div className="connection-title">
            <ConnectionBrandIcon id="cliq" label="Zoho Cliq" />
            <div>
              <h2>Zoho Cliq</h2>
              <p className="muted tight">Zoho One team chat — channels and DMs in Hub Chat.</p>
            </div>
          </div>
          <span className="pill">
            {cliqAccounts.length
              ? `${cliqAccounts.length} connected`
              : cliqConfigured
                ? "Ready"
                : "Setup"}
          </span>
        </div>
        {cliqAccounts.length ? (
          <ul className="simple-list">
            {cliqAccounts.map((account) => (
              <li key={account.id} className="connector-row">
                <div>
                  <strong>{chatAccountLabel(account)}</strong>
                  {account.email ? <p className="muted tight">{account.email}</p> : null}
                </div>
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={Boolean(busyProvider)}
                  onClick={() => void disconnectAccount(account.id, "cliq")}
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">
            {cliqConfigured
              ? "No Cliq org linked yet. Connect one — Connect again adds another without replacing."
              : "Add Zoho Cliq OAuth client credentials on the API host, then Connect. Same Zoho Accounts OAuth family as other Zoho apps."}
          </p>
        )}
        {!cliqConfigured ? (
          <ol className="settings-steps">
            <li>
              In Zoho API Console, create a Server-based client for Hub.
            </li>
            <li>
              Redirect URL: <code>https://api.hub.atw.net/oauth/cliq/callback</code>.
            </li>
            <li>
              Scopes: <code>ZohoCliq.Channels.READ</code>, <code>ZohoCliq.Messages.READ</code>,
              chats/users read as needed.
            </li>
            <li>
              Store <code>CLIQ_CLIENT_ID</code> / <code>CLIQ_CLIENT_SECRET</code> in{" "}
              <code>~/.config/atw-hub/cliq.env</code> and on Lightsail.
            </li>
            <li>Restart the Hub API and Connect Cliq here.</li>
          </ol>
        ) : null}
        <div className="settings-btn-row">
          <button
            type="button"
            className="primary-btn"
            disabled={Boolean(busyProvider) || !cliqConfigured}
            onClick={() => void connectProvider("cliq")}
          >
            {busyProvider === "cliq"
              ? "Opening…"
              : cliqAccounts.length
                ? "Connect Another Cliq"
                : "Connect Cliq"}
          </button>
        </div>
      </section>

      <section className="settings-card" id="zoom-chat">
        <div className="connector-row">
          <div className="connection-title">
            <ConnectionBrandIcon id="zoom" label="Zoom Team Chat" />
            <div>
              <h2>Zoom Team Chat</h2>
              <p className="muted tight">Zoom Team Chat channels and direct messages in Hub Chat.</p>
            </div>
          </div>
          <span className="pill">
            {zoomAccounts.length ? `${zoomAccounts.length} connected` : zoomConfigured ? "Ready" : "Setup"}
          </span>
        </div>
        {zoomAccounts.length ? (
          <ul className="simple-list">
            {zoomAccounts.map((account) => (
              <li key={account.id} className="connector-row">
                <div>
                  <strong>{chatAccountLabel(account)}</strong>
                  {account.email ? <p className="muted tight">{account.email}</p> : null}
                </div>
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={Boolean(busyProvider)}
                  onClick={() => void disconnectAccount(account.id, "zoom")}
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">
            {zoomConfigured ? "No Zoom account connected yet." : "Zoom Team Chat isn't available yet."}
          </p>
        )}
        <div className="settings-btn-row">
          <button
            type="button"
            className="primary-btn"
            disabled={Boolean(busyProvider) || !zoomConfigured}
            onClick={() => void connectProvider("zoom")}
          >
            {busyProvider === "zoom"
              ? "Opening…"
              : zoomAccounts.length
                ? "Connect Another Zoom"
                : "Connect Zoom Chat"}
          </button>
        </div>
      </section>

      <section className="settings-card" id="teams">
        <div className="connector-row">
          <div className="connection-title">
            <ConnectionBrandIcon id="teams" label="Microsoft Teams" />
            <div>
              <h2>Microsoft Teams</h2>
              <p className="muted tight">Microsoft Teams chats in Hub Chat, for work or school accounts.</p>
            </div>
          </div>
          <span className="pill">
            {teamsAccounts.length ? `${teamsAccounts.length} connected` : teamsConfigured ? "Ready" : "Setup"}
          </span>
        </div>
        {teamsAccounts.length ? (
          <ul className="simple-list">
            {teamsAccounts.map((account) => (
              <li key={account.id} className="connector-row">
                <div>
                  <strong>{chatAccountLabel(account)}</strong>
                  {account.email ? <p className="muted tight">{account.email}</p> : null}
                </div>
                <button
                  type="button"
                  className="secondary-btn"
                  disabled={Boolean(busyProvider)}
                  onClick={() => void disconnectAccount(account.id, "teams")}
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">
            {teamsConfigured
              ? "No Teams account connected yet. Connect with a Microsoft 365 work or school account."
              : "Microsoft Teams isn't available yet."}
          </p>
        )}
        <div className="settings-btn-row">
          <button
            type="button"
            className="primary-btn"
            disabled={Boolean(busyProvider) || !teamsConfigured}
            onClick={() => void connectProvider("teams")}
          >
            {busyProvider === "teams"
              ? "Opening…"
              : teamsAccounts.length
                ? "Connect Another Teams"
                : "Connect Teams"}
          </button>
        </div>
      </section>
    </>
  );
}

export function ConnectionsCalendarTab() {
  const {
    accounts,
    busy,
    loading,
    startGoogleConnect,
    startAtwMailConnect,
    setNotice,
    setError,
    refresh,
  } = useConnections();
  const [searchParams, setSearchParams] = useSearchParams();
  const gmailAccounts = accounts.filter((a) => (a.providerId || "gmail") === "gmail");
  const atwMailAccounts = accounts.filter((a) => a.providerId === "atw-mail");
  const atwCalendarLinked = atwMailAccounts.filter(
    (a) => a.calendarConnected || a.providerId === "atw-mail",
  );
  const calendarLinked = gmailAccounts.filter((a) => a.calendarConnected);
  const reconnectParam = (searchParams.get("reconnect") || "").trim();
  const reconnectStarted = useRef(false);
  const resumedAtwCalendar = useRef(false);

  // Same ATW Mail grant hash as the Mail tab, when consent returns to Calendar.
  useEffect(() => {
    if (resumedAtwCalendar.current) return;
    const prefix = "#atw-mail=";
    const hash = window.location.hash;
    if (!hash.startsWith(prefix)) return;
    resumedAtwCalendar.current = true;
    let grants: { email?: string; grant: string }[] = [];
    try {
      const parsed = JSON.parse(decodeURIComponent(hash.slice(prefix.length))) as {
        grants?: { email?: string; grant: string }[];
      };
      grants = Array.isArray(parsed.grants) ? parsed.grants : [];
    } catch {
      setError("ATW Calendar did not return a connection.");
      return;
    }
    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
    void (async () => {
      try {
        const result = await connectAtwMailGrants(grants);
        setNotice(
          result.message.includes("connected")
            ? `${result.message} ATW Calendar uses the same mailbox link.`
            : result.message,
        );
        await refresh();
      } catch (error) {
        setError(error instanceof Error ? error.message : "Could not connect ATW Calendar.");
      }
    })();
  }, [refresh, setError, setNotice]);

  // Tip 139: Calendar "Reconnect" deep link → open Google OAuth for that account.
  useEffect(() => {
    if (loading || !reconnectParam || reconnectStarted.current || busy) return;
    const linked = gmailAccounts.filter((a) => a.calendarConnected);
    const target =
      reconnectParam === "1"
        ? linked[0] || gmailAccounts[0]
        : gmailAccounts.find(
            (a) => a.email.toLowerCase() === reconnectParam.toLowerCase(),
          ) || linked[0];
    if (!target) {
      setNotice("Connect Google Mail first, then reconnect Calendar.");
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("reconnect");
          return next;
        },
        { replace: true },
      );
      return;
    }
    reconnectStarted.current = true;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("reconnect");
        return next;
      },
      { replace: true },
    );
    setNotice(`Reconnecting calendar for ${target.email}…`);
    void startGoogleConnect({ email: target.email, includeCalendar: true });
  }, [
    loading,
    reconnectParam,
    busy,
    gmailAccounts,
    startGoogleConnect,
    setNotice,
    setSearchParams,
  ]);

  const totalLinked = calendarLinked.length + atwCalendarLinked.length;

  return (
    <>
      <section className="settings-card" id="calendar-connectors">
        <div className="settings-card-head">
          <h2>Calendar Connectors</h2>
          <span className="pill">
            {totalLinked ? `${totalLinked} Linked` : "Available"}
          </span>
        </div>
        <p className="muted">
          Connect ATW Calendar through ATW Mail (required first), or link Google Calendar from a
          Google mail account. Use Reconnect if access expired.
        </p>

        <article className="settings-inner-card" id="atw-calendar">
          <div className="settings-card-head">
            <div className="connection-title">
              <ConnectionBrandIcon id="atw-mail" label="ATW Calendar" />
              <div>
                <h3>ATW Calendar</h3>
                <p className="muted tight">ATW WorkSuite</p>
              </div>
            </div>
            <span className={`pill ${atwCalendarLinked.length ? "pill-connected" : ""}`}>
              {atwCalendarLinked.length
                ? atwCalendarLinked.length === 1
                  ? "1 Connected"
                  : `${atwCalendarLinked.length} Connected`
                : "Available"}
            </span>
          </div>
          <p className="muted">
            ATW Calendar uses your ATW Mail mailbox. Connect Mail first; Calendar rides the same
            Hub grant. Shared mailbox Calendar access is controlled in Mail Admin → Shared.
          </p>
          {loading ? (
            <p className="muted">Loading…</p>
          ) : atwMailAccounts.length === 0 ? (
            <div className="account-row" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
              <p className="muted tight" style={{ flex: "1 1 12rem", margin: 0 }}>
                Mail is not connected. Calendar cannot be set up without Mail.
              </p>
              <button
                type="button"
                className="settings-connect-btn"
                disabled={Boolean(busy)}
                onClick={() =>
                  void startAtwMailConnect({ returnPath: "/settings/connections/calendar" })
                }
              >
                {busy === "connect-atw-mail" ? "Connecting…" : "Connect ATW Mail First"}
              </button>
              <Link className="settings-connect-btn" to="/settings/connections/mail">
                Open Mail Connections
              </Link>
            </div>
          ) : (
            <ul className="account-list">
              {atwMailAccounts.map((account) => (
                <li key={account.id} className="account-row">
                  <div className="account-row-main">
                    <div className="connection-title">
                      <ConnectionBrandIcon id="atw-mail" label="ATW Mail" />
                      <div>
                        <strong className="mail-email-truncate" title={account.email}>
                          {resolveAccountLabel(account)}
                        </strong>
                        <p className="muted tight">
                          {account.calendarConnected !== false
                            ? "Calendar connected with Mail"
                            : "Mail linked; enable Calendar"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="settings-connect-btn"
                    disabled={Boolean(busy)}
                    onClick={() =>
                      void startAtwMailConnect({ returnPath: "/settings/connections/calendar" })
                    }
                  >
                    {busy === "connect-atw-mail" ? "Connecting…" : "Reconnect"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="settings-inner-card" id="google-calendar">
          <div className="settings-card-head">
            <div className="connection-title">
              <ConnectionBrandIcon id="google-calendar" label="Google Calendar" />
              <div>
                <h3>Google Calendar</h3>
                <p className="muted tight">Google</p>
              </div>
            </div>
            <span className={`pill ${calendarLinked.length ? "pill-connected" : ""}`}>
              {calendarLinked.length
                ? calendarLinked.length === 1
                  ? "1 Connected"
                  : `${calendarLinked.length} Connected`
                : "Available"}
            </span>
          </div>
          <p className="muted">
            Calendar access is managed from each Google mail account in Connections.
          </p>
          {gmailAccounts.length === 0 ? (
            <Link className="settings-connect-btn" to="/settings/connections/mail">
              Connect Google Mail First
            </Link>
          ) : (
            <ul className="account-list">
              {gmailAccounts.map((account) => (
                <li key={account.id} className="account-row">
                  <div className="account-row-main">
                    <div className="connection-title">
                      <ConnectionBrandIcon id="gmail" label="Google" />
                      <div>
                        <strong className="mail-email-truncate" title={account.email}>
                          {resolveAccountLabel(account)}
                        </strong>
                        <p className="muted tight">
                          {account.calendarConnected
                            ? "Calendar connected"
                            : "Calendar not linked"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {!account.calendarConnected ? (
                    <button
                      type="button"
                      className="settings-connect-btn"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        void startGoogleConnect({
                          email: account.email,
                          includeCalendar: true,
                        })
                      }
                    >
                      {busy === `cal-${account.email}` ? "Opening Google…" : "Connect"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="settings-connect-btn"
                      disabled={Boolean(busy)}
                      onClick={() =>
                        void startGoogleConnect({
                          email: account.email,
                          includeCalendar: true,
                        })
                      }
                    >
                      {busy === `cal-${account.email}` ? "Opening Google…" : "Reconnect"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>
    </>
  );
}

export function ConnectionsMeetingsTab() {
  const { accounts, busy, setNotice, startGoogleConnect } = useConnections();
  const [zoom, setZoom] = useState<Awaited<ReturnType<typeof fetchZoomMeetingsStatus>> | null>(null);
  const [zoomBusy, setZoomBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchZoomMeetingsStatus()
      .then((next) => {
        if (!cancelled) setZoom(next);
      })
      .catch(() => {
        if (!cancelled) setZoom(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const googleAccounts = accounts.filter(
    (account) => account.providerId === "gmail" || !account.providerId,
  );
  const meetingReady = googleAccounts.filter(
    (account) => account.meetingsConnected || account.calendarConnected,
  );

  async function connectZoom() {
    setZoomBusy(true);
    try {
      const result = await startZoomMeetingsConnect();
      const url = result.authorizationUrl;
      if (url) {
        window.location.href = url;
        return;
      }
      setNotice(result.message || "Zoom did not return a sign-in URL.");
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Could not start Zoom connect.");
    } finally {
      setZoomBusy(false);
    }
  }

  return (
    <>
      <section className="settings-card" id="google-meet">
        <div className="settings-card-head">
          <div className="connection-title">
            <ConnectionBrandIcon id="google" label="Google Meet" />
            <div>
              <h2>Google Meet</h2>
              <p className="muted tight">Scheduled Meet links from Google Calendar</p>
            </div>
          </div>
          <span className={`pill ${meetingReady.length ? "pill-connected" : ""}`}>
            {meetingReady.length ? "Connected" : "Not Connected"}
          </span>
        </div>
        <p className="muted">
          Hub meetings are one list. Google Meet rows are Calendar events that include a Meet link.
          Enable the Google Calendar API and Google Meet API on the OAuth client before connecting.
        </p>
        {googleAccounts.length ? (
          <ul className="simple-list">
            {googleAccounts.map((account) => (
              <li key={account.id}>
                <div>
                  <strong>{account.email}</strong>
                  <p className="muted tight">
                    {account.calendarConnected ? "Calendar on" : "Calendar off"}
                    {" · "}
                    {account.meetingsConnected ? "Meetings on" : "Meetings off"}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No Google account connected yet.</p>
        )}
        <div className="settings-btn-row">
          <button
            type="button"
            className="primary-btn"
            disabled={Boolean(busy)}
            onClick={() =>
              void startGoogleConnect({
                services: { mail: false, calendar: true, chat: false, meetings: true },
              })
            }
          >
            {busy === "connect-google" ? "Opening Google…" : "Connect Google Meetings"}
          </button>
          <Link className="secondary-btn settings-link-btn" to="/meetings">
            Open Meetings
          </Link>
        </div>
      </section>

      <section className="settings-card" id="zoom">
        <div className="settings-card-head">
          <div className="connection-title">
            <ConnectionBrandIcon id="zoom" label="Zoom" />
            <div>
              <h2>Zoom</h2>
              <p className="muted tight">Upcoming Zoom meetings in the same list</p>
            </div>
          </div>
          <span className={`pill ${zoom?.connected ? "pill-connected" : ""}`}>
            {zoom?.connected ? "Connected" : "Not Connected"}
          </span>
        </div>
        <p className="muted">
          {zoom?.connected
            ? `Signed in${zoom.accountLabel ? ` as ${zoom.accountLabel}` : ""}. Upcoming meetings appear in Meetings.`
            : "Connect Zoom to add its upcoming meetings next to Google Meet."}
        </p>
        <div className="settings-btn-row">
          <button type="button" className="primary-btn" disabled={zoomBusy} onClick={() => void connectZoom()}>
            {zoomBusy ? "Opening Zoom…" : zoom?.connected ? "Reconnect Zoom" : "Connect Zoom"}
          </button>
        </div>
      </section>
    </>
  );
}

export function ConnectionsAiTab() {
  const [status, setStatus] = useState<HubAiStatus | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchHubAiStatus()
      .then((next) => {
        if (!cancelled) setStatus(next);
      })
      .catch((err) => {
        if (!cancelled) setError(hubAiErrorMessage(err, "Couldn't load Hub AI."));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const slack = status?.slack;
  const llm = status?.llm;
  const slackDetail = !slack
    ? "Checking…"
    : slack.connected
      ? `${slack.teamName || "Slack"}${slack.userName ? ` · @${slack.userName}` : ""}`
      : slack.configured
        ? "Not connected to Hub AI yet. Hub Chat's Slack connection is separate."
        : "The Hub AI Slack app isn't set up on the Hub API yet.";
  const llmDetail = !llm
    ? "Checking…"
    : llm.configured
      ? `${hubAiProviderLabel(llm.provider)} · ${llm.model || "default model"}`
      : "No AI model configured. An admin adds OPENAI_API_KEY or ANTHROPIC_API_KEY to the Hub API.";

  return (
    <section className="settings-card" id="connections-ai">
      <div className="settings-card-head">
        <div className="connection-title">
          <span className="brand-icon brand-icon-ai connections-ai-mark" aria-hidden>
            <Sparkles size={16} />
          </span>
          <div>
            <h2>Hub AI</h2>
            <p className="muted tight">Assistant for your Slack, through Slack's official MCP server</p>
          </div>
        </div>
        <span className={`pill ${slack?.connected && llm?.configured ? "pill-connected" : ""}`}>
          {!status ? (error ? "Unavailable" : "Loading…") : slack?.connected && llm?.configured ? "Ready" : "Setup"}
        </span>
      </div>
      {error ? (
        <p className="banner-error" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="simple-list">
        <li className="connector-row">
          <div className="connection-title">
            <ConnectionBrandIcon id="slack" label="Slack" />
            <div>
              <strong>Slack</strong>
              <p className="muted tight">{slackDetail}</p>
            </div>
          </div>
          <span className={`pill ${slack?.connected ? "pill-connected" : ""}`}>
            {slack?.connected ? "Connected" : "Not Connected"}
          </span>
        </li>
        <li className="connector-row">
          <div className="connection-title">
            <span className="brand-icon brand-icon-ai connections-ai-mark" aria-hidden>
              <Sparkles size={14} />
            </span>
            <div>
              <strong>AI model</strong>
              <p className="muted tight">{llmDetail}</p>
            </div>
          </div>
          <span className={`pill ${llm?.configured ? "pill-connected" : ""}`}>
            {llm?.configured ? "Ready" : "Not Configured"}
          </span>
        </li>
      </ul>
      <div className="settings-btn-row">
        <Link className="primary-btn settings-link-btn" to="/ai">
          {slack?.configured && !slack.connected ? "Connect Slack in Hub AI" : "Open Hub AI"}
        </Link>
      </div>
    </section>
  );
}
